/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';

export type Props = {
  /**
   * Content to render inside of `Badge`. This should only contain numeric
   * content! Its type is `string`, however, to support shorthand and formatted
   * content such as "10k" and "10,000"
   */
  children: string;
  /** Optional property for overriding styles. */
  className?: string;
  /**
   * The color variant of `Badge`.
   * @default 'neutral-stronger'
   * */
  variant?:
    | 'neutral'
    | 'primary'
    | 'brand'
    | 'success'
    | 'warning'
    | 'error'
    | 'neutral-stronger'
    | 'primary-stronger'
    | 'brand-stronger'
    | 'success-stronger'
    | 'warning-stronger'
    | 'error-stronger';
};

const Styles = labelStyles('Badge', {
  root: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: var(--zds-size-xsmall);
    min-width: var(--zds-size-small);
    padding: var(--zds-space-2) var(--zds-space-8);
    font: var(--zds-small-print-3);
    border-radius: var(--zds-radius-pill);

    &[data-variant='neutral'] {
      background-color: var(--zds-background-strongest);
      color: var(--zds-text-default);
    }

    &[data-variant='primary'] {
      background-color: var(--zds-ui-primary-weaker);
      color: var(--zds-text-default);
    }

    &[data-variant='brand'] {
      background-color: var(--zds-ui-brand-weaker);
      color: var(--zds-text-default);
    }

    &[data-variant='success'] {
      background-color: var(--zds-status-success-weaker);
      color: var(--zds-text-default);
    }

    &[data-variant='warning'] {
      background-color: var(--zds-status-warning-weakest);
      color: var(--zds-text-default);
    }

    &[data-variant='error'] {
      background-color: var(--zds-status-error-weaker);
      color: var(--zds-text-default);
    }

    &[data-variant='neutral-stronger'] {
      background-color: var(--zds-gray-warm-10);
      color: var(--zds-text-inverted);
    }

    &[data-variant='primary-stronger'] {
      background-color: var(--zds-ui-primary-stronger);
      color: var(--zds-text-inverted);
    }

    &[data-variant='brand-stronger'] {
      background-color: var(--zds-ui-brand-stronger);
      color: var(--zds-text-inverted);
    }

    &[data-variant='success-stronger'] {
      background-color: var(--zds-status-success-stronger);
      color: var(--zds-text-inverted);
    }

    &[data-variant='warning-stronger'] {
      background-color: var(--zds-status-warning-weaker);
      color: var(--zds-text-default);
    }

    &[data-variant='error-stronger'] {
      background-color: var(--zds-status-error-stronger);
      color: var(--zds-text-inverted);
    }
  `,
});

/**
 * `Badge` renders a small tag component to be used solely for numeric labels.
 *
 * When using this alongside another component, be sure to include the number and what it relates to in a label or aria-label for accessibility.
 */
export const Badge = ({
  className,
  variant = 'neutral-stronger',
  children,
}: Props) => {
  return (
    <span
      className={className}
      css={Styles.root}
      data-variant={variant}
      data-zds
    >
      {children}
    </span>
  );
};
